import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Loader, Table} from '../../../Components';
import {
    Button,
    Flex,
    Icon,
    PriorityIcon,
    TableColumn,
    Text
} from '@priority-software/priority-style-react';
import {observer, useObserver} from 'mobx-react';
import texts from '../../../texts';
import styles from './CompareCashierBalancesStep.module.scss';
import {getCashBalSubForm, getCashForm} from '../../../actions/compareCashierBalancesStep';
import {NextState} from '../../../types';
import {useFooterContext} from '../../../hooks';
import classnames from 'classnames';
import {exitSubForm} from '../../../../Form/actions/form';
import _ from 'lodash';
import {priority} from '@priority-software/priority-proto-api/lib/nform';
import {Form, formsStore, Record} from '../../../../../stores/Form';

const cashColumnNames = ['CASHNAME', 'CASHDES', 'BALANCE3'];

const replaceAll = (value: string) => _.replace(value, /,/g, '');

export const CompareCashierBalancesStep: FC = observer(() => {
    const [cashSum, setCashSum] = useState<(number | string)[]>();
    const [cashFormLoading, setCashFormLoading] = useState(false);
    const footerState = useFooterContext();
    const [cashForm, setCashForm] = useState<Form | null>(null);

    const cashRecords = useObserver(() => cashForm?.records.filter(x => !x.isNew));

    const tableColumns: TableColumn[] = useObserver(() =>
        cashForm
            ? [
                  ...cashForm.columns
                      .filter(column => cashColumnNames.includes(column.Name))
                      .map(column => {
                          return {
                              key: column.Title,
                              title: column.Title,
                              dataIndex: column.Title,
                              render: (_, row: Record) => {
                                  const value = row.getField(column.id).currentValue;
                                  const forceLtr =
                                      column.Type !== priority.netitems.form.ColumnType.Str &&
                                      column.Type !== priority.netitems.form.ColumnType.Rstr;
                                  return (
                                      <Text
                                          tag={'span'}
                                          skin={'paragraph-2'}
                                          componentClasses={classnames({['forceLtr']: forceLtr})}
                                      >
                                          {value}
                                      </Text>
                                  );
                              }
                          } as TableColumn;
                      }),
                  {
                      key: texts['stage_1_step1_column_cashiers'],
                      title: texts['stage_1_step1_column_cashiers'],
                      dataIndex: texts['stage_1_step1_column_cashiers'],
                      render: (_, row: Record, index) => {
                          return (
                              <Text tag={'span'} skin={'paragraph-2'}>
                                  {typeof cashSum[index] === 'string'
                                      ? cashSum[index]
                                      : (cashSum[index] as number).toLocaleString('en', {
                                            minimumFractionDigits: 2
                                        })}
                              </Text>
                          );
                      }
                  } as TableColumn
              ]
            : []
    );

    const tableVisible = !cashFormLoading && !!cashRecords && !!cashSum;

    const refreshData = () => {
        setCashSum(undefined);
        setCashFormLoading(true);
        getCashForm().then(async form => {
            setCashForm(form);
            const cashSum = [];

            for (const record of form.records.filter(x => !x.isNew)) {
                form.setCurrentRow(record.recordIndex);
                const cashSubForm = await getCashBalSubForm(form);
                const balanceColumnId = cashSubForm.columns.find(x => x.Name === 'BALANCE').id;
                const codeColumnId = cashSubForm.columns.find(x => x.Name === 'CODE').id;
                const subRecords = cashSubForm.records
                    .filter(x => !x.isNew)
                    .filter(
                        x =>
                            x.getField(codeColumnId).currentValue ===
                            record.getField(form.columns.find(y => y.Name === 'CODE').id)
                                .currentValue
                    );
                let sum;
                if (cashSubForm.records.filter(x => !x.isNew).length === subRecords.length) {
                    sum = subRecords
                        .map(x =>
                            !!x.getField(balanceColumnId).currentValue
                                ? Number.parseFloat(
                                      replaceAll(x.getField(balanceColumnId).currentValue as string)
                                  )
                                : 0
                        )
                        .reduce((res, current) => res + current, 0);
                } else {
                    sum = texts['stage_1_step1_several_currencies'];
                }
                cashSum.push(sum);
                await exitSubForm(form.session);
            }

            setCashSum(cashSum);
            setCashFormLoading(false);
        });
    };

    useEffect(() => {
        refreshData();
    }, []);

    useEffect(() => {
        if (footerState.onNextState === NextState.Processing) {
            formsStore.deleteForm(cashForm.session);
            footerState.setOnNextState(NextState.Done);
        }
    }, [footerState.onNextState]);

    useEffect(() => {
        footerState.setIsValid(!cashFormLoading);
    }, [cashFormLoading]);

    const cashForBalanceColumn = useMemo(
        () => cashForm && cashForm.columns.find(y => y.Name === 'BALANCE3'),
        [cashForm?.columns]
    );

    const isRowBalanced = useCallback(
        (index: number) => {
            if (cashSum && cashRecords && cashRecords.length > index && cashForBalanceColumn) {
                const value = cashRecords[index].getField(cashForBalanceColumn.id).currentValue;
                return value !== undefined
                    ? Number.parseFloat(replaceAll(value as string)) === cashSum[index]
                    : cashSum[index] === 0;
            } else {
                return false;
            }
        },
        [cashSum, cashRecords, cashForBalanceColumn]
    );

    const unbalancedCount =
        cashSum && cashRecords?.filter((_, index) => !isRowBalanced(index)).length;

    return (
        <Flex
            componentClasses={classnames(styles.wrapper, {
                [styles.wrapperFullHeight]: tableVisible
            })}
            alignItems={'flex-start'}
            direction={'column'}
        >
            <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                {texts['stage1_step1_explanation']}
            </Text>
            <Flex componentClasses={styles.info}>
                {!cashFormLoading && cashForm ? (
                    unbalancedCount !== 0 ? (
                        <>
                            {unbalancedCount > 0 && (
                                <Text
                                    tag={'span'}
                                    skin={'paragraph-1'}
                                    componentClasses={styles.unbalancedLabel}
                                    textColor={'primary-700'}
                                >
                                    {unbalancedCount} {texts['unbalanced']}
                                </Text>
                            )}
                            {cashRecords.length - unbalancedCount > 0 && (
                                <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                                    {cashRecords.length - unbalancedCount} {texts['balanced']}
                                </Text>
                            )}
                        </>
                    ) : (
                        <Flex
                            direction={'row'}
                            justifyContent={'flex-start'}
                            componentClasses={styles.allCashiersBalancedLabel}
                        >
                            <Icon iconColor={'validation-green'} icon={PriorityIcon.roundCheck} />
                            <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                                {texts['all_P1_cashiers_balanced'].replace(
                                    '<P1>',
                                    cashRecords?.length
                                )}
                            </Text>
                        </Flex>
                    )
                ) : null}
                <Button
                    componentClasses={styles.button}
                    disabled={cashFormLoading}
                    skin={'secondary'}
                    size={'medium'}
                    onClick={() => refreshData()}
                >
                    {texts['refresh_data']}
                </Button>
            </Flex>
            {tableVisible && (
                <Table
                    rowClassName={row => {
                        return isRowBalanced(row.recordIndex) ? undefined : styles.unbalancedRow;
                    }}
                    rowKey={obj => obj.recordIndex}
                    componentClasses={styles.table}
                    columns={tableColumns}
                    data={cashRecords}
                />
            )}
            <Loader isVisible={cashFormLoading} componentClassname={styles.loader} />
        </Flex>
    );
});
